import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { autoevolution } from "../../components/funtionsBD/Mainservice";
import { getLiveTrainingPortalRoot } from "../../hooks/useLiveFullscreen";

const PERF_COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#10b981"];
const EXERTION_COLORS = ["#22c55e", "#84cc16", "#eab308", "#f97316", "#ef4444"];
const PERF_ICONS = ["😞", "😕", "😐", "🙂", "🤩"];
const EXERTION_ICONS = ["💤", "🚶", "🏃", "🔥", "💥"];

function RatingRow({ value, onChange, colors, icons, label }) {
  return (
    <div className="lt-ae__group">
      <label className="lt-ae__label">{label}</label>
      <div className="lt-ae__row">
        {[1, 2, 3, 4, 5].map((level) => {
          const active = value === level;
          const color = colors[level - 1];
          return (
            <button
              key={level}
              type="button"
              className={`lt-ae__btn${active ? " lt-ae__btn--active" : ""}`}
              style={
                active
                  ? { backgroundColor: color, borderColor: color }
                  : { borderColor: `${color}66` }
              }
              onClick={() => onChange(level)}
            >
              <span className="lt-ae__emoji">{icons[level - 1]}</span>
              <span className="lt-ae__num">{level}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Player auto-evaluation sheet (performance + exertion).
 * Portals into the Live Training fullscreen root so it stays visible on mobile.
 */
export function LiveTrainingAutoevalSheet({
  open,
  exercise,
  title,
  subtitle,
  playerName = "",
  isLandscape = false,
  smartRequired = false,
  onRated,
  onSubmitted,
  onSkip,
  celebrateOnly = false,
  onCelebrateDone,
}) {
  const { t } = useTranslation();
  const [perf, setPerf] = useState(null);
  const [exertion, setExertion] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setCelebrate(false);
    setPerf(null);
    setExertion(null);
    setError(null);
    if (!celebrateOnly) return undefined;
    setCelebrate(true);
    const id = window.setTimeout(() => {
      setCelebrate(false);
      onCelebrateDone?.();
    }, 1200);
    return () => window.clearTimeout(id);
  }, [open, celebrateOnly, onCelebrateDone]);

  if (!open) return null;
  if (!celebrateOnly && !onRated && !exercise) return null;

  const resolvedPlayerName = String(playerName || "").trim();
  const showPlayerName = Boolean(resolvedPlayerName && resolvedPlayerName !== "—");
  const headingText =
    title ??
    (smartRequired
      ? t("liveTrainingAutoevalSmartRequired", "Auto-evaluation required (SMART)")
      : t("liveTrainingAutoevalPrompt"));
  const exerciseName =
    exercise?.ex_name || exercise?.exercise?.ex_name || t("exercise");
  const subtitleText =
    subtitle ??
    (exercise
      ? [
          exerciseName,
          smartRequired
            ? t(
                "ae_live_personalized_hint",
                "This is the priority for this cycle — performance and effort."
              )
            : t(
                "liveTrainingAutoevalSoftHint",
                "Rate your performance and how hard it felt."
              ),
        ].join(" · ")
      : "");
  const canSubmit = perf && exertion && !submitting && !celebrateOnly;

  const handleSubmit = async () => {
    if (celebrateOnly) return;
    if (!canSubmit) return;

    if (onRated) {
      const ratedPerf = perf;
      const ratedExertion = exertion;
      setCelebrate(true);
      window.setTimeout(() => {
        setCelebrate(false);
        setPerf(null);
        setExertion(null);
        onRated(ratedPerf, ratedExertion);
      }, 1200);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const clamp = (n) => Math.min(5, Math.max(1, Math.round(Number(n)) || 1));
      await autoevolution({
        te_id: exercise.te_id,
        exercise_perf: String(clamp(perf)),
        perceived_exertion: String(clamp(exertion)),
      });
      setSubmitting(false);
      setCelebrate(true);
      window.setTimeout(() => {
        setCelebrate(false);
        setPerf(null);
        setExertion(null);
        onSubmitted?.();
      }, 1200);
    } catch (err) {
      const msg = String(err?.message || "");
      if (msg.toLowerCase().includes("already")) {
        setSubmitting(false);
        setCelebrate(true);
        window.setTimeout(() => {
          setCelebrate(false);
          setPerf(null);
          setExertion(null);
          onSubmitted?.();
        }, 1200);
        return;
      }
      setSubmitting(false);
      setError(
        t("liveTrainingAutoevalSaveError", "Could not save your evaluation. Try again.")
      );
    }
  };

  const portalRoot = getLiveTrainingPortalRoot();
  if (!portalRoot) return null;

  const sheet = (
    <div
      className={`lt-sheet-backdrop lt-ae-backdrop${
        isLandscape ? " lt-ae-backdrop--landscape" : ""
      }`}
      role="presentation"
    >
      <div
        className={`lt-sheet lt-ae${celebrate ? " lt-ae--celebrate" : ""}${
          isLandscape ? " lt-ae--landscape" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lt-ae-title"
      >
        <div className="lt-ae__sparkles" aria-hidden={!celebrate} />
        <div className="lt-ae__body">
          <h3 id="lt-ae-title" className="lt-sheet__title">
            {headingText}
          </h3>
          {showPlayerName ? (
            <p className="lt-ae__player" aria-live="polite">
              {t("liveTrainingAutoevalForPlayer", "For {{name}}", {
                name: resolvedPlayerName,
              })}
            </p>
          ) : null}
          {subtitleText ? (
            <p className="lt-sheet__subtitle">{subtitleText}</p>
          ) : null}

          {!celebrateOnly ? (
            <div className="lt-ae__ratings">
              <RatingRow
                value={perf}
                onChange={setPerf}
                colors={PERF_COLORS}
                icons={PERF_ICONS}
                label={`🎯 ${t("ae_performance")}`}
              />
              <RatingRow
                value={exertion}
                onChange={setExertion}
                colors={EXERTION_COLORS}
                icons={EXERTION_ICONS}
                label={`💪 ${t("ae_exertion")}`}
              />
            </div>
          ) : null}
          {error ? <p className="lt-ae__error">{error}</p> : null}
        </div>

        <div className="lt-ae__footer">
          <button
            type="button"
            className="lt-sheet__submit"
            disabled={!canSubmit && !celebrate}
            onClick={handleSubmit}
          >
            {celebrate ? "🎉" : submitting ? t("submitting") : t("submit")}
          </button>
          {!celebrateOnly && !smartRequired && onSkip ? (
            <button
              type="button"
              className="lt-sheet__skip"
              disabled={submitting}
              onClick={() => onSkip?.()}
            >
              {t("liveTrainingAutoevalSkip", "Skip for now")}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, portalRoot);
}

export default LiveTrainingAutoevalSheet;
